import * as stylex from "@stylexjs/stylex";
import { Button } from "components/button";
import { format } from "d3-format";
import { ArrowLeft14 } from "icons/ArrowLeft";
import { QuestionMark16 } from "icons/QuestionMark";
import { forwardRef, useCallback, useEffect } from "react";
import { defineMessage, FormattedMessage } from "react-intl";
import { colors } from "../figures/TRAJECTORIES_SIMULATION_LMIC";
import useForwardRef from "../hooks/useForwardRef";
import { typography } from "../materials/typography.stylex";
const DefaultVal = ({ children })=>{
    return <span className="x13rv2e4">{children}</span>;
};
const stylexStyles = {
    slideTitle: {
        k1K539: "xhbfen4",
        $$css: true
    },
    slide: {
        kLWn49: "x1evy7pa",
        $$css: true
    }
};
export const defaultValues = {
    country: <DefaultVal>Switzerland</DefaultVal>,
    comparisonCountry: <DefaultVal>Italy</DefaultVal>,
    age9CountryValue: <DefaultVal>12%</DefaultVal>,
    age9ComparisonValue: <DefaultVal>15%</DefaultVal>,
    countryDifference: <DefaultVal>3%</DefaultVal>,
    skillVerb: <DefaultVal>read</DefaultVal>,
    skillName: <DefaultVal>reading</DefaultVal>,
    accessName: <strong style={{
        color: colors.accessSimulation
    }}>Access Simulation</strong>,
    accessChange: <DefaultVal>an increase</DefaultVal>,
    accessDifference: <DefaultVal>12</DefaultVal>,
    learningName: <strong style={{
        color: colors.learningSimulation
    }}>Learning Simulation</strong>,
    learningChange: <DefaultVal>a decrease</DefaultVal>,
    learningChangeAdverb: <DefaultVal>greater</DefaultVal>,
    learningDifference: <DefaultVal>14</DefaultVal>
};
export const Highlight = ({ children })=><span>{children}</span>;
const formatRoundPerc = format(".0%");
const formatRound = format("d");
export const formatPerc = (d)=>formatRoundPerc(d || 0);
export const formatPercentagePoints = (d, intl)=>{
    return `${formatRound(d !== null ? d * 100 : 0)} ${intl.formatMessage(defineMessage({
        id: "percentage points",
        defaultMessage: "percentage points"
    }))}`;
};
const messageDescriptors = (x)=>x;
export const i18nLookup = messageDescriptors({
    increase: defineMessage({
        id: "fig.learning.trajectories.interpretation.increase",
        defaultMessage: "an increase"
    }),
    decrease: defineMessage({
        id: "fig.learning.trajectories.interpretation.decrease",
        defaultMessage: "a decrease"
    }),
    noChange: defineMessage({
        id: "fig.learning.trajectories.interpretation.no-change",
        defaultMessage: "no change"
    }),
    greater: defineMessage({
        id: "fig.learning.trajectories.interpretation.greater",
        defaultMessage: "greater"
    }),
    lower: defineMessage({
        id: "fig.learning.trajectories.interpretation.lower",
        defaultMessage: "lower"
    }),
    higher: defineMessage({
        id: "fig.learning.trajectories.interpretation.higher",
        defaultMessage: "higher"
    }),
    less: defineMessage({
        id: "fig.learning.trajectories.interpretation.less",
        defaultMessage: "less"
    }),
    more: defineMessage({
        id: "fig.learning.trajectories.interpretation.more",
        defaultMessage: "more"
    }),
    averageLowLowerCountries: defineMessage({
        id: "fig.learning.trajectories.interpretation.averageLowLowerCountries",
        defaultMessage: "the average low and lower-middle income country"
    }),
    "skillName.LIT": defineMessage({
        id: "fig.learning.trajectories.interpretation.skillName.LIT",
        defaultMessage: "reading"
    }),
    "skillName.NUM": defineMessage({
        id: "fig.learning.trajectories.interpretation.skillName.NUM",
        defaultMessage: "numeracy"
    }),
    "skillVerb.LIT": defineMessage({
        id: "fig.learning.trajectories.interpretation.skillVerb.LIT",
        defaultMessage: "read"
    }),
    "skillVerb.NUM": defineMessage({
        id: "fig.learning.trajectories.interpretation.skillVerb.NUM",
        defaultMessage: "do basic math"
    })
});
export const makeDisparityGroupLabels = (intl)=>({
        WEALTH: [
            intl.formatMessage(defineMessage({
                id: "trajectories.disparities.WEALTH.left",
                defaultMessage: "children from poor families"
            })),
            intl.formatMessage(defineMessage({
                id: "trajectories.disparities.WEALTH.right",
                defaultMessage: "children from wealthy families"
            }))
        ],
        GENDER: [
            intl.formatMessage(defineMessage({
                id: "trajectories.disparities.GENDER.left",
                defaultMessage: "girls"
            })),
            intl.formatMessage(defineMessage({
                id: "trajectories.disparities.GENDER.right",
                defaultMessage: "boys"
            }))
        ],
        GEOGRAPHY: [
            intl.formatMessage(defineMessage({
                id: "trajectories.disparities.GEOGRAPHY.left",
                defaultMessage: "children from rural areas"
            })),
            intl.formatMessage(defineMessage({
                id: "trajectories.disparities.GEOGRAPHY.right",
                defaultMessage: "children from urban areas"
            }))
        ]
    });
export const InterpretationToken = ({ children, highlightSeries, highlightPoint, chartActions, color })=>{
    const handleFocus = useCallback(()=>{
        if (highlightSeries) {
            chartActions.setHighlightedSeries(highlightSeries);
        }
        if (highlightPoint) {
            chartActions.setLockActivePoint(highlightPoint);
        }
    }, [
        highlightSeries,
        highlightPoint,
        chartActions
    ]);
    const handleBlur = useCallback(()=>{
        chartActions.setHighlightedSeries(undefined);
        chartActions.setLockActivePoint(undefined);
    }, [
        chartActions
    ]);
    const tokenProps = {
        className: "x13grs9u xbvplod xrldrom x1277o0a x882633 xycbk1u x11vpbzd"
    };
    return <span {...tokenProps} onFocus={handleFocus} onMouseOver={handleFocus} onBlur={handleBlur} onMouseOut={handleBlur} style={{
        ...tokenProps.style,
        fontWeight: "bold",
        color
    }}>
      {children}
    </span>;
};
export const CarouselFooter = ({ paginate, page, count })=>{
    return <div className="x78zum5 x1qughib">
      {page === 0 ? <div/> : <Button rounded inverted inline ghost onClick={()=>paginate(-1)}>
          <ArrowLeft14 className="xpoiemb"/>
          <FormattedMessage id="Back" defaultMessage="Back"/>
        </Button>}
      {page !== count - 1 ? <Button rounded style={{
        paddingLeft: "1rem",
        paddingRight: "1rem",
        minWidth: "auto"
    }} inline onClick={()=>paginate(+1)}>
          <FormattedMessage id="next" defaultMessage="Next"/>
        </Button> : null}
    </div>;
};
/**
 * Custom renderer for the MultiCheckbox control used to
 * show the Data interpretation button above the chart.
 *
 * The button wiggles when it is in the viewport.
 */ export const ToggleInterpretationButton = forwardRef(({ handler, label, value, isSelected, animation }, ref)=>{
    const localRef = useForwardRef(ref);
    useEffect(()=>{
        if (!localRef.current) {
            return;
        }
        const observer = new IntersectionObserver((entries)=>{
            const entry = entries[0];
            if (entry.intersectionRatio > 0) {
                localRef.current?.classList.add("inView");
            } else {
                localRef.current?.classList.remove("inView");
            }
        });
        observer.observe(localRef.current);
        return ()=>{
            observer.disconnect();
        };
    }, [
        localRef
    ]);
    return <Button ref={localRef} // The pulse-on-scroll-into-view animation is driven by the `inView` class, toggled
    // imperatively via `classList` above (an IntersectionObserver callback, outside
    // React render) — since that class never passes through props, StyleX can't scope a
    // rule to it; `.toggle-interpretation-button.inView`/`[data-focus]` live in
    // style.global.css instead, scoped by this marker class.
    className={[
        isSelected ? "opened" : undefined,
        "toggle-interpretation-button",
        animation === false ? "toggle-interpretation-button--no-pulse" : undefined,
        {
            className: "x1w9im6i x78zum5 xl56j7k"
        }.className
    ].filter(Boolean).join(" ")} // padding/width moved to `style` — Button's own StyleX styles set both, and StyleX's
    // boosted specificity always wins over an externally injected class for the same
    // property, but inline styles always beat StyleX classes.
    style={{
        padding: "5px",
        width: "calc(100% + 1rem)"
    }} rounded data-focus={isSelected ? "focused" : undefined} onClick={()=>handler({
            target: {
                value
            }
        })}>
      <QuestionMark16 className="xpoiemb"/>
      {label}
    </Button>;
});
export const EmptyInterpretation = ({ children })=>{
    return <div {...stylex.props(typography.interpretation)}>{children}</div>;
};
export const SlideTitle = ({ children })=>{
    return <h4 {...stylex.props(typography.heading4, stylexStyles.slideTitle)}>{children}</h4>;
};
export const Slide = ({ children })=>{
    return <div {...stylex.props(typography.interpretation, stylexStyles.slide)}>{children}</div>;
};
